import { Card, H6 } from '@blueprintjs/core';
import { showSaveSuccessToast } from 'Components/Generic/BOSToasterUtil';
import { OBJECT_STATUS, user_org_roles } from 'Data/Enum';
import UserOrgRoleData from 'Data/UserOrgRoleData';
import DataManager from 'Lib/DataManager';
import { useStateWithRef } from 'Lib/hooks/useStateWithRef';
import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import styles from 'Styles/styles.module.css';
import { areEquivalent, getNameWithInactiveTag, groupToMap } from 'Util';
import OrganizationSelect from '../BOSFilterSelect/OrganizationSelect';
import UserSelect from '../BOSFilterSelect/UserSelect';
import AddNewButton from '../Buttons/AddNewButton';
import DetailPanel from '../DetailPanel';
import NoContentBox from '../Misc/NoContentBox';

let DM = new DataManager();

const UserOrgRolesPanel = ({
  userId,
  orgId,
  editable = false,
  title = 'Core Roles',
  detailsOnly = false,
  ...props
}) => {
  const [refreshTrigger, setRefreshTrigger] = useState(false);
  const [rolesList, setRolesList] = useState([]);
  const [groupByCore, setGroupByCore, groupByCoreRef] = useStateWithRef(true);
  const [rolesMap, setRolesMap] = useState(new Map());
  const [groupMap, setGroupMap] = useState(new Map());
  const [showNewRoleLine, setShowNewRoleLine] = useState(false);
  const [newRoleObject, setNewRoleObject] = useState({});

  useEffect(() => {
    if (UserOrgRoleData) {
      DM.add(
        UserOrgRoleData.registerListener('LIST_LOADED', onRolesLoaded),
        UserOrgRoleData,
      );
    }
    if (UserOrgRoleData) {
      DM.add(
        UserOrgRoleData.registerListener('RECORD_CREATED', onRoleSaved),
        UserOrgRoleData,
      );
    }
    if (UserOrgRoleData) {
      DM.add(
        UserOrgRoleData.registerListener('RECORDS_DELETED', onRoleRemoved),
        UserOrgRoleData,
      );
    }
    return () => {
      DM.clear();
    };
  }, []);

  useEffect(() => {
    fetchRoleList();
  }, [refreshTrigger]);

  useEffect(() => {
    if (userId) {
      setGroupByCore(true);
      fetchRoleList();
    }
  }, [userId]);

  useEffect(() => {
    if (orgId) {
      setGroupByCore(false);
      fetchRoleList();
    }
  }, [orgId]);

  const fetchRoleList = () => {
    if (userId) {
      UserOrgRoleData.getRecordList({
        where: { '*user_id': userId },
      });
    } else if (orgId) {
      UserOrgRoleData.getRecordList({
        where: { '*organization_id': orgId },
      });
    }
  };

  const onRolesLoaded = (payload) => {
    if (payload?.records) {
      let rolesListSorted = [...payload.records].filter(
        (roleRecord) => !!roleRecord.user,
      );
      if (groupByCoreRef.current) {
        rolesListSorted.sort((a, b) =>
          a.organization.name.localeCompare(b.organization.name),
        );
      } else {
        rolesListSorted.sort((a, b) =>
          a.user.full_name.localeCompare(b.user.full_name),
        );
      }
      setRolesList(rolesListSorted);
    }
  };

  useEffect(() => {
    if (rolesList) {
      if (groupByCore) {
        setRolesMap(groupToMap(rolesList, 'organization_id'));
        setGroupMap(
          rolesList.reduce(
            (entryMap, e) =>
              entryMap.set(e.organization_id, {
                ...(entryMap.get(e.organization_id) || e.organization),
              }),
            new Map(),
          ),
        );
      } else {
        setRolesMap(groupToMap(rolesList, 'user_id'));
        setGroupMap(
          rolesList.reduce(
            (entryMap, e) =>
              entryMap.set(e.user_id, {
                ...(entryMap.get(e.user_id) || e.user),
              }),
            new Map(),
          ),
        );
      }
    }
  }, [rolesList, groupByCore]);

  const onRoleSaved = () => {
    showRolesSavedToast();
  };

  const onRoleRemoved = () => {
    showRolesSavedToast();
  };

  const showRolesSavedToast = () => {
    setRefreshTrigger((prevState) => !prevState);
    setNewRoleObject({});
    setShowNewRoleLine(false);
    showSaveSuccessToast('Core Roles Updated');
  };

  const onRolesChanged = (roleObject, value, isNew = false) => {
    let roleToSave = {
      ...roleObject,
      ...(userId && { user_id: userId }),
      ...(orgId && { organization_id: orgId }),
    };
    if (isNew) {
      roleToSave = { ...roleToSave, ...newRoleObject };
    }
    if (value) {
      UserOrgRoleData.createRoleRecord(roleToSave);
    } else {
      UserOrgRoleData.deleteRoleRecord(roleToSave);
    }
  };

  let getRoleRow = ({ rolesForRow, userId, orgId }, isNew = false) => (
    <Card style={{ paddingTop: 6, paddingBottom: 6 }}>
      <div
        style={{
          paddingTop: 6,
          display: 'flex',
          flexDirection: 'column',
          gap: 6,
          justifyContent: 'space-evenly',
        }}>
        {editable &&
          Object.values(user_org_roles).map((user_org_role, i) => {
            return (
              <label key={i} className='bp5-control bp5-switch bp5-align-right'>
                <input
                  type='checkbox'
                  disabled={
                    !editable || (isNew && areEquivalent(newRoleObject, {}))
                  }
                  checked={!isNew && rolesForRow?.includes(user_org_role.key)}
                  onChange={(event) => {
                    onRolesChanged(
                      {
                        user_id: userId,
                        organization_id: orgId,
                        role: user_org_role.key,
                      },
                      event.target.checked,
                      isNew,
                    );
                  }}
                />
                <span className='bp5-control-indicator'></span>
                {user_org_role.label}
              </label>
            );
          })}
        {!editable &&
          rolesForRow?.length &&
          Object.values(rolesForRow).map((user_role_key, i) => {
            return (
              <label
                key={i}
                className='bp5-control bp5-switch bp5-align-right'
                style={{ cursor: 'default' }}>
                <input
                  type='checkbox'
                  readOnly
                  style={{ cursor: 'default' }}
                  checked
                />
                <span
                  className='bp5-control-indicator'
                  style={{ cursor: 'default' }}></span>
                {
                  Object.values(user_org_roles).find(
                    (user_role) => user_role.key === user_role_key,
                  ).label
                }
              </label>
            );
          })}
      </div>
    </Card>
  );

  let getGroupHeader = (id) =>
    groupByCore ? (
      groupMap.get(id).name
    ) : (
      <span
        className={
          groupMap.get(id).status === OBJECT_STATUS.DEACTIVATED.key &&
          styles.inactive
        }>
        {getNameWithInactiveTag(groupMap.get(id), 'full_name')}
      </span>
    );

  let groupedRolesContent =
    !editable && !rolesList?.length ? (
      <NoContentBox label='No Core Users' />
    ) : (
      [...rolesMap.entries()].map(([groupId, roles], i) => {
        return (
          <div
            key={i}
            style={{
              minWidth: 300,
            }}>
            <H6>{getGroupHeader(groupId)}</H6>
            {getRoleRow({
              rolesForRow: roles.map((role) => role.role),
              ...(groupByCore && { orgId: groupId }),
              ...(!groupByCore && { userId: groupId }),
            })}
          </div>
        );
      })
    );

  let newRoleLine = (
    <div
      style={{
        width: '60%',
        minWidth: 300,
        display: 'flex',
        flexDirection: 'column',
        gap: 6,
      }}>
      {groupByCore ? (
        <OrganizationSelect
          excludedIds={Array.from(groupMap.keys())}
          onSelectOrganization={(org) =>
            setNewRoleObject(org ? { organization_id: org.id } : {})
          }
        />
      ) : (
        <UserSelect
          excludedIds={Array.from(groupMap.keys())}
          onSelectUser={(user) =>
            setNewRoleObject(user ? { user_id: user.id } : {})
          }
        />
      )}
      {getRoleRow({}, true)}
    </div>
  );

  const newButton = editable && (
    <AddNewButton
      clickHandler={() => setShowNewRoleLine(true)}
      buttonText={groupByCore ? 'Add Core Role' : 'Add User'}
    />
  );

  const footer = !showNewRoleLine && newButton;

  let panelContent = (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'end',
        gap: 10,
      }}>
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 20,
          width: 300,
        }}>
        {groupedRolesContent}
        {showNewRoleLine && newRoleLine}
      </div>
      {footer}
    </div>
  );

  return detailsOnly ? (
    panelContent
  ) : (
    <DetailPanel title={title} editable={editable} collapsible {...props}>
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 20,
          width: '100%',
        }}>
        {panelContent}
      </div>
    </DetailPanel>
  );
};

UserOrgRolesPanel.propTypes = {
  userId: PropTypes.number,
  orgId: PropTypes.number,
  editable: PropTypes.bool,
  title: PropTypes.string,
  detailsOnly: PropTypes.bool,
};

export default UserOrgRolesPanel;
